
import React from 'react';
import { Editor } from '@tiptap/react';
import { Video } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface VideoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  videoUrl: string;
  setVideoUrl: (url: string) => void;
  addVideo: () => void;
  editor: Editor;
}

const VideoDialog = ({ 
  isOpen, 
  onOpenChange, 
  videoUrl, 
  setVideoUrl, 
  addVideo,
  editor 
}: VideoDialogProps) => {
  // Validation de l'URL vidéo
  const isValidVideoUrl = () => {
    if (!videoUrl) return false;
    
    // Vérifie si c'est une URL YouTube, Vimeo ou Dailymotion
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const dailymotionRegex = /(?:dailymotion\.com\/video|dai\.ly)\/([a-zA-Z0-9]+)/;
    
    return youtubeRegex.test(videoUrl) || vimeoRegex.test(videoUrl) || dailymotionRegex.test(videoUrl);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" /> Ajouter une vidéo
          </DialogTitle>
          <DialogDescription>
            Collez l'URL d'une vidéo YouTube, Vimeo ou Dailymotion pour l'intégrer dans votre contenu.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="video-url" className="text-right">
              URL
            </Label>
            <Input
              id="video-url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="col-span-3"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValidVideoUrl()) {
                  addVideo();
                }
              }}
            />
          </div>
          {videoUrl && !isValidVideoUrl() && (
            <p className="text-red-500 text-sm">
              Veuillez saisir une URL valide de YouTube, Vimeo ou Dailymotion.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            type="button" 
            onClick={addVideo} 
            disabled={!isValidVideoUrl()}
          >
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoDialog;
